import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { ProductCard } from "@/components/ProductCard";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { Search, SlidersHorizontal, X } from "lucide-react";
import { formatINR } from "@/lib/api";

const categories = [
  { label: "All Chains", value: "All" },
  { label: "Gold", value: "Gold" },
  { label: "Silver", value: "Silver" },
  { label: "Gold Plated", value: "GP" },
];

const MAX_PRICE = 350000;

function Filters({ search, setSearch, category, setCategory, priceRange, setPriceRange, availability, setAvailability, onReset }) {
  return (
    <div className="space-y-8" data-testid="filters-panel">
      <div>
        <label className="overline text-obsidian block mb-3">Category</label>
        <div className="flex flex-col gap-1">
          {categories.map((c) => (
            <button
              key={c.value}
              onClick={() => setCategory(c.value)}
              className={`text-left py-1.5 text-sm transition-colors ${category === c.value ? "text-gold-dark font-semibold" : "text-muted-foreground hover:text-obsidian"}`}
              data-testid={`filter-cat-${c.value}`}
            >
              {c.label}
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className="overline text-obsidian block mb-4">Price Range</label>
        <Slider
          value={priceRange}
          onValueChange={setPriceRange}
          min={0}
          max={MAX_PRICE}
          step={500}
          className="mb-3"
          data-testid="price-slider"
        />
        <div className="flex justify-between text-xs text-muted-foreground">
          <span>{formatINR(priceRange[0])}</span>
          <span>{formatINR(priceRange[1])}</span>
        </div>
      </div>

      <div>
        <label className="overline text-obsidian block mb-3">Availability</label>
        <Select value={availability} onValueChange={setAvailability}>
          <SelectTrigger data-testid="availability-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="in_stock">In Stock</SelectItem>
            <SelectItem value="out_of_stock">Out of Stock</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <button onClick={onReset} className="text-sm text-gold-dark font-semibold hover:underline" data-testid="reset-filters">
        Reset Filters
      </button>
    </div>
  );
}

export default function Catalogue() {
  const [searchParams] = useSearchParams();
  const [products, setProducts] = useState(null);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState(searchParams.get("category") || "All");
  const [sort, setSort] = useState("newest");
  const [priceRange, setPriceRange] = useState([0, MAX_PRICE]);
  const [availability, setAvailability] = useState("all");

  useEffect(() => {
    const cat = searchParams.get("category");
    if (cat) setCategory(cat);
  }, [searchParams]);

  const fetchProducts = useCallback(async () => {
    setProducts(null);
    const params = { sort, category };
    if (search) params.search = search;
    if (priceRange[0] > 0) params.min_price = priceRange[0];
    if (priceRange[1] < MAX_PRICE) params.max_price = priceRange[1];
    if (availability !== "all") params.availability = availability;
    try {
      const { data } = await api.get("/products", { params });
      setProducts(data);
    } catch {
      setProducts([]);
    }
  }, [sort, category, search, priceRange, availability]);

  useEffect(() => {
    const t = setTimeout(fetchProducts, 300);
    return () => clearTimeout(t);
  }, [fetchProducts]);

  const reset = () => {
    setSearch("");
    setCategory("All");
    setPriceRange([0, MAX_PRICE]);
    setAvailability("all");
    setSort("newest");
  };

  const filterProps = { search, setSearch, category, setCategory, priceRange, setPriceRange, availability, setAvailability, onReset: reset };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-14" data-testid="catalogue-page">
      <div className="mb-8">
        <p className="overline text-gold-dark mb-2">Catalogue</p>
        <h1 className="font-serif text-4xl lg:text-5xl text-obsidian">Gold & Silver Chains</h1>
      </div>

      {/* Top bar */}
      <div className="flex flex-col sm:flex-row gap-3 mb-8">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search by name or SKU..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 rounded-sm"
            data-testid="search-input"
          />
        </div>
        <div className="flex gap-3">
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger className="w-full sm:w-48 rounded-sm" data-testid="sort-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="newest">Newest</SelectItem>
              <SelectItem value="popularity">Popularity</SelectItem>
              <SelectItem value="price_low">Price: Low to High</SelectItem>
              <SelectItem value="price_high">Price: High to Low</SelectItem>
            </SelectContent>
          </Select>

          {/* Mobile filter */}
          <Sheet>
            <SheetTrigger asChild>
              <button className="lg:hidden inline-flex items-center gap-2 px-4 border border-black/20 rounded-sm text-sm font-semibold" data-testid="mobile-filter-btn">
                <SlidersHorizontal className="w-4 h-4" /> Filters
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-80 overflow-y-auto">
              <SheetHeader className="mb-6"><SheetTitle className="font-serif text-2xl">Filters</SheetTitle></SheetHeader>
              <Filters {...filterProps} />
            </SheetContent>
          </Sheet>
        </div>
      </div>

      <div className="grid lg:grid-cols-[220px_1fr] gap-10">
        <aside className="hidden lg:block">
          <div className="sticky top-24">
            <Filters {...filterProps} />
          </div>
        </aside>

        <div>
          {(() => {
            if (products === null) {
              return (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-10">
                  {["s1", "s2", "s3", "s4", "s5", "s6"].map((k) => (
                    <div key={k}>
                      <Skeleton className="aspect-[4/5] w-full" />
                      <Skeleton className="h-4 w-2/3 mt-4" />
                      <Skeleton className="h-6 w-1/2 mt-2" />
                    </div>
                  ))}
                </div>
              );
            }
            if (products.length === 0) {
              return (
                <div className="text-center py-24" data-testid="no-products">
                  <X className="w-10 h-10 mx-auto text-muted-foreground mb-4" />
                  <p className="font-serif text-2xl text-obsidian mb-2">No chains found</p>
                  <p className="text-sm text-muted-foreground">Try adjusting your filters or search terms.</p>
                </div>
              );
            }
            return (
              <>
                <p className="text-sm text-muted-foreground mb-6" data-testid="results-count">{products.length} products</p>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-10">
                  {products.map((p, i) => (
                    <ProductCard key={p.id} product={p} index={i} />
                  ))}
                </div>
              </>
            );
          })()}
        </div>
      </div>
    </div>
  );
}
